import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import userApi from '_api/user'
import { getUserInfo, setUserInfo } from '_utils/localData'

const ProfileTab = ({ currentUser = {}, onUserUpdated }) => {
  const { addToast } = useToasts()
  const [isPayWithUEN, setIsPayWithUEN] = useState(
    Boolean(currentUser?.isPayWithUEN)
  )
  const [isUpdatingUenPay, setIsUpdatingUenPay] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      postalCode: '',
      uen: '',
    },
  })

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 0) {
      reset({
        name: currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        postalCode: currentUser.postalCode || '',
        uen: currentUser.uen || '',
      })
      setIsPayWithUEN(Boolean(currentUser.isPayWithUEN))
    }
  }, [currentUser, reset])

  const handleSwitchChange = async () => {
    if (isUpdatingUenPay) return
    if (!currentUser?.uen) {
      addToast('Please fill your uen', { appearance: 'error', autoDismiss: true })
      return
    }
    try {
      setIsUpdatingUenPay(true)
      const nextVal = !isPayWithUEN
      await userApi.updatePayWithUen({ isPayWithUEN: nextVal })
      const cached = getUserInfo() || {}
      const updatedUser = { ...cached, ...currentUser, isPayWithUEN: nextVal }
      setUserInfo(updatedUser)
      setIsPayWithUEN(nextVal)
      if (onUserUpdated) onUserUpdated(updatedUser)
      addToast('Update success', { appearance: 'success', autoDismiss: true })
    } catch (e) {
      addToast('Something went wrong', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsUpdatingUenPay(false)
    }
  }

  const onSubmit = async (values) => {
    try {
      const { name, phone, postalCode, uen } = values

      const payload = {
        name: (name || '').trim(),
        phone: (phone || '').trim(),
        postalCode: (postalCode || '').trim(),
        uen: (uen || '').trim(),
      }

      await userApi.updateUser(payload)
      if (payload.uen && !isPayWithUEN) {
        await userApi.updatePayWithUen({ isPayWithUEN: false })
      }
      const { msgResp: updatedUser } = await userApi.getUser()
      if (updatedUser) {
        setUserInfo(updatedUser)
        setIsPayWithUEN(Boolean(updatedUser.isPayWithUEN))
        if (onUserUpdated) onUserUpdated(updatedUser)
      }
      addToast('Profile saved successfully', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (error) {
      addToast(error?.msgResp || 'Failed to update profile', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  return (
    <div className='tab-profile-view'>
      <div className='section-card'>
        <div className='section-hd'>
          <div className='section-title'>
            <svg
              width='15'
              height='15'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
              <circle cx='12' cy='7' r='4' />
            </svg>
            Personal Information
          </div>
        </div>

        <form className='profile-form' onSubmit={handleSubmit(onSubmit)}>
          <div className='field-group'>
            <label className='field-label'>Name</label>
            <input
              className='field-input'
              placeholder='Your name'
              {...register('name')}
            />
          </div>

          <div className='field-group'>
            <label className='field-label'>Email</label>
            <input
              className='field-input'
              type='email'
              readOnly
              style={{ background: '#FAFAF8', cursor: 'not-allowed' }}
              placeholder='Email address'
              {...register('email')}
            />
          </div>

          <div className='field-group'>
            <label className='field-label'>Phone / WhatsApp</label>
            <input
              className='field-input'
              type='tel'
              placeholder='Phone number'
              {...register('phone')}
            />
          </div>

          <div className='field-group'>
            <label className='field-label'>Postal Code</label>
            <input
              className='field-input'
              placeholder='Postal code'
              {...register('postalCode')}
            />
          </div>

          <div className='field-group'>
            <label className='field-label'>UEN (Optional)</label>
            <input
              className='field-input'
              placeholder='UEN'
              {...register('uen')}
            />
          </div>

          <div className='field-switch'>
            <button
              type='button'
              role='switch'
              id='payWithUEN'
              aria-checked={isPayWithUEN}
              disabled={isUpdatingUenPay}
              className={`userv2-toggle-switch ${isPayWithUEN ? 'on' : ''}`}
              onClick={handleSwitchChange}
            >
              <span className='toggle-thumb' />
            </button>
            <label
              htmlFor='payWithUEN'
              className='switch-label'
              onClick={(e) => {
                e.preventDefault()
                handleSwitchChange()
              }}
            >
              QR Code Payment with UEN
            </label>
          </div>

          <button type='submit' className='save-btn' disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ProfileTab
